import {
    type ToolExecutionEntry,
    type ToolOutputRenderer,
} from '@apps/web/components/phoebe_chat_view';
import { Button } from '@apps/web/components/ui/button';
import { cn } from '@apps/web/lib/utils';
import { type AdminAgentPayloadFile } from '@apps/web/routes/_app/admin/agent/payload_viewer_shared';
import { SkillOverrideChangeCard } from '@apps/web/routes/_app/admin/agent/skill_override_change_card';
import {
    CodeRenderer,
    DiffRenderer,
} from '@apps/web/routes/_app/admin/agent/tool_output_code_diff_renderer';
import {
    ConsoleOutputView,
    formatConsoleDuration,
    JsonRenderer,
    MarkdownRenderer,
    TableRenderer,
} from '@apps/web/routes/_app/admin/agent/tool_output_data_renderer';
import {
    selectAdminToolOutput,
    selectAdminToolOutputMatch,
} from '@apps/web/routes/_app/admin/agent/tool_output_renderer_selection';
import {
    type AdminToolOutputSelection,
    type AdminToolOutputVariant,
    isRecord,
} from '@apps/web/routes/_app/admin/agent/tool_output_renderer_shared';

export { selectAdminToolOutput } from '@apps/web/routes/_app/admin/agent/tool_output_renderer_selection';

type AdminToolOutputContentProps = {
    readonly selection: AdminToolOutputSelection;
    readonly variant: AdminToolOutputVariant;
    readonly filename?: string | null;
};

type AdminToolTruncationMarker = {
    readonly artifact_ref?: {
        readonly artifact_id: string;
        readonly title: string;
    };
    readonly dropped_count?: number;
    readonly dropped_unit?: string;
    readonly kind?: string;
    readonly label?: string;
    readonly message?: string;
};

function formatCountLabel(
    count: number,
    singular: string,
    plural: string = `${singular}s`,
): string {
    return `${count.toLocaleString()} ${count === 1 ? singular : plural}`;
}

function truncateCollapsedDetail(value: string, maxChars: number = 88): string {
    const normalized = value.replace(/\s+/g, ' ').trim();
    if (normalized.length <= maxChars) {
        return normalized;
    }
    return `${normalized.slice(0, maxChars - 1).trimEnd()}…`;
}

function summarizeJsonValue(value: unknown): string {
    if (Array.isArray(value)) {
        return `${formatCountLabel(value.length, 'item')} in structured JSON`;
    }
    if (value && typeof value === 'object') {
        return `${formatCountLabel(
            Object.keys(value).length,
            'field',
        )} in structured JSON`;
    }
    return 'Structured JSON';
}

function summarizeAdminToolOutputSelection(
    selection: AdminToolOutputSelection,
): string | null {
    switch (selection.kind) {
        case 'code': {
            const lineCount = selection.code.split('\n').length;
            return [
                selection.language?.toUpperCase() ?? 'Code',
                formatCountLabel(lineCount, 'line'),
            ].join(' · ');
        }
        case 'console':
            return [
                `exit ${selection.exitCode}`,
                selection.timedOut ? 'timed out' : null,
                selection.durationMs !== null
                    ? formatConsoleDuration(selection.durationMs)
                    : null,
                selection.stepName ? `step ${selection.stepName}` : null,
            ]
                .filter((part): part is string => part !== null)
                .join(' · ');
        case 'diff': {
            const lineCount = selection.text.split('\n').length;
            return [
                selection.files.length > 0
                    ? formatCountLabel(selection.files.length, 'file')
                    : null,
                formatCountLabel(lineCount, 'line'),
            ]
                .filter((part): part is string => part !== null)
                .join(' diff · ');
        }
        case 'json':
            return summarizeJsonValue(selection.value);
        case 'markdown':
            return (
                truncateCollapsedDetail(selection.markdown) || 'Markdown output'
            );
        case 'table':
            return selection.summaryLabel
                ? `Tabular result · ${selection.summaryLabel}`
                : 'Tabular result';
        default: {
            const exhaustive: never = selection;
            return exhaustive;
        }
    }
}

function AdminToolOutputContent({
    filename = null,
    selection,
    variant,
}: AdminToolOutputContentProps) {
    switch (selection.kind) {
        case 'code':
            return (
                <CodeRenderer
                    code={selection.code}
                    language={selection.language}
                    variant={variant}
                />
            );
        case 'console':
            return (
                <ConsoleOutputView
                    stdout={selection.stdout}
                    stderr={selection.stderr}
                    exitCode={selection.exitCode}
                    durationMs={selection.durationMs}
                    stepName={selection.stepName}
                    timedOut={selection.timedOut}
                    outputTruncated={selection.outputTruncated}
                    raw={selection.raw}
                />
            );
        case 'diff':
            return (
                <DiffRenderer
                    files={selection.files}
                    text={selection.text}
                    variant={variant}
                />
            );
        case 'json':
            return (
                <JsonRenderer
                    filename={filename}
                    value={selection.value}
                    variant={variant}
                />
            );
        case 'markdown':
            return (
                <MarkdownRenderer
                    markdown={selection.markdown}
                    variant={variant}
                />
            );
        case 'table':
            return (
                <TableRenderer
                    columns={selection.columns}
                    rows={selection.rows}
                    summaryLabel={selection.summaryLabel}
                    variant={variant}
                />
            );
        default: {
            const exhaustive: never = selection;
            return exhaustive;
        }
    }
}

function truncationMarkersFromOutput(
    output: unknown,
): ReadonlyArray<AdminToolTruncationMarker> {
    if (!isRecord(output) || !Array.isArray(output['truncation_markers'])) {
        return [];
    }
    return output['truncation_markers'].filter(isRecord);
}

function AdminToolTruncationMarkers({
    onSelectArtifact,
    output,
}: {
    readonly onSelectArtifact?: ((artifactId: string) => void) | null;
    readonly output: unknown;
}) {
    const markers = truncationMarkersFromOutput(output);
    if (markers.length === 0) {
        return null;
    }

    return (
        <div className="space-y-2">
            {markers.map((marker, index) => {
                const artifactRef = isRecord(marker.artifact_ref)
                    ? marker.artifact_ref
                    : null;
                const artifactId =
                    artifactRef && typeof artifactRef.artifact_id === 'string'
                        ? artifactRef.artifact_id
                        : null;
                const artifactTitle =
                    artifactRef && typeof artifactRef.title === 'string'
                        ? artifactRef.title
                        : 'artifact';
                const markerKey =
                    typeof marker.kind === 'string'
                        ? `${marker.kind}-${index}`
                        : `marker-${index}`;
                return (
                    <div
                        key={markerKey}
                        className="rounded-[2px] border border-[var(--aa-warning-border)] bg-[var(--aa-warning-soft)] px-3 py-2"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 text-[12px] leading-5 text-[var(--aa-warning)]">
                                {typeof marker.message === 'string'
                                    ? marker.message
                                    : 'Visible output was truncated; the full result is in the artifact.'}
                            </div>
                            {artifactId && onSelectArtifact ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 shrink-0 px-2 text-[11px] text-[var(--aa-warning)] hover:bg-[var(--aa-warning-border)]"
                                    onClick={() => onSelectArtifact(artifactId)}
                                >
                                    Open artifact
                                </Button>
                            ) : null}
                        </div>
                        {artifactId ? (
                            <div className="mt-1 font-mono text-[10px] break-all text-[var(--aa-warning)]">
                                {artifactTitle} · {artifactId}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

export function AdminAgentToolOutputInline({
    entry,
    onSelectArtifact = null,
}: {
    readonly entry: ToolExecutionEntry;
    readonly onSelectArtifact?: ((artifactId: string) => void) | null;
}) {
    const selection = selectAdminToolOutput({
        output: entry.output,
        text: entry.text,
    });
    if (!selection) {
        return null;
    }
    return (
        <div className="space-y-2">
            <AdminToolOutputContent selection={selection} variant="inline" />
            <AdminToolTruncationMarkers
                onSelectArtifact={onSelectArtifact}
                output={entry.output}
            />
        </div>
    );
}

export function AdminAgentToolOutputFile({
    file,
}: {
    readonly file: AdminAgentPayloadFile;
}) {
    const selection = selectAdminToolOutput({
        output: file.value,
        filename: file.filename,
    });
    if (!selection) {
        return (
            <div className="px-3 py-3 text-sm text-[var(--aa-ink-muted)]">
                No payload content.
            </div>
        );
    }
    return (
        <div className="min-h-0 flex-1 overflow-auto bg-[var(--aa-surface)] p-3">
            <AdminToolOutputContent
                filename={file.filename}
                selection={selection}
                variant="file"
            />
        </div>
    );
}

export function createAdminToolOutputRenderer({
    onSelectArtifact = null,
}: {
    readonly onSelectArtifact?: ((artifactId: string) => void) | null;
} = {}): ToolOutputRenderer {
    return function renderAdminToolOutput(entry: ToolExecutionEntry) {
        const skillVersionId = skillOverrideVersionId(entry);
        if (skillVersionId !== null) {
            return {
                kind: 'custom-tool-output',
                content: <SkillOverrideChangeCard versionId={skillVersionId} />,
                collapsedDetail: 'Active skill version with raw diff',
                defaultExpanded: true,
                suppressText: true,
            };
        }
        const match = selectAdminToolOutputMatch({
            output: entry.output,
            text: entry.text,
        });
        if (!match) {
            return null;
        }
        const selection = match.selection;
        return {
            kind: 'custom-tool-output',
            content: (
                <div
                    className={cn(
                        'border-t border-[var(--aa-border)] pt-2',
                        selection.kind === 'markdown' && 'pt-1',
                    )}
                >
                    <AdminToolOutputContent
                        selection={selection}
                        variant="inline"
                    />
                    <AdminToolTruncationMarkers
                        onSelectArtifact={onSelectArtifact}
                        output={entry.output}
                    />
                </div>
            ),
            collapsedDetail: summarizeAdminToolOutputSelection(selection),
            defaultExpanded: !entry.isComplete,
            suppressText: !match.keepTextSummary,
        };
    };
}

function skillOverrideVersionId(entry: ToolExecutionEntry): string | null {
    if (entry.name !== 'propose_skill_update' || !isRecord(entry.output)) {
        return null;
    }
    const result = isRecord(entry.output['result'])
        ? entry.output['result']
        : entry.output;
    const versionId = result['version_id'];
    return typeof versionId === 'string' && versionId.length > 0
        ? versionId
        : null;
}
